import { TenantRole, User } from '@/api/generatedApi';
import { AddUserModal } from '@/components/modals/AddUserModal/AddUserModal';
import { BaseContextMenu } from '@/components/ContextMenu/BaseContextMenu';
import { CopyMenuItem, getCopyValueFromColumn } from '@/components/ContextMenu/CopyMenuItem';
import { DeleteUserModal } from '@/components/modals/DeleteUserModal';
import { TitleText } from '@/components/TitleText';
import { useFetchTenantUsers } from '@/hooks/api/users/useFetchTenantUsers';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { useUpdateUser } from '@/hooks/api/users/useUpdateUser';
import { useContextMenu } from '@/hooks/useContextMenu';
import { useToast } from '@/hooks/useToast';
import { HeaderBar } from '@/components/HeaderBar';
import { TitleTextActionButton } from '@/components/TitleTextActionButton';
import { CopiableGridColDef } from '@/types/copiableColumn';
import { Add } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { Box, Checkbox, IconButton, MenuItem, Select, Tooltip } from '@mui/material';
import { GridRenderCellParams, useGridApiRef } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { DataGridLazy } from '../DataGrid/DataGridLazy';
import { Ellipsis } from '../Ellipsis';
import { capitalize } from '@/utils/capitalize';

const CANNOT_UPDATE_CURRENT_USER =
  'Updating your own user settings is not allowed. Please contact another administrator.';

const RoleCell = (parameters: GridRenderCellParams<User>) => {
  const { showToast } = useToast();
  const { isUserCurrentUser } = useGetCurrentUser();
  const { updateUser, isUpdating } = useUpdateUser({ userId: parameters.row.id });

  const isCurrentUser = isUserCurrentUser(parameters.row);
  const WrapperComponent = isCurrentUser ? Tooltip : Box;

  return (
    <WrapperComponent title={isCurrentUser ? CANNOT_UPDATE_CURRENT_USER : ''}>
      <Select
        value={parameters.row.settings.tenantRole}
        onChange={event => {
          updateUser(
            { tenantRole: event.target.value as TenantRole },
            {
              onSuccess: () => {
                parameters.api.updateRows([
                  {
                    ...parameters.row,
                    settings: {
                      ...parameters.row.settings,
                      tenantRole: event.target.value as TenantRole,
                    },
                  },
                ]);
                showToast({ message: 'User role updated', type: 'success' });
              },
              onError: () => showToast({ message: 'Failed to update user role', type: 'error' }),
            }
          );
        }}
        disabled={isUpdating || isCurrentUser}
        size='small'
        fullWidth
      >
        {Object.values(TenantRole).map(role => (
          <MenuItem key={role} value={role}>
            {capitalize(role)}
          </MenuItem>
        ))}
      </Select>
    </WrapperComponent>
  );
};

const EnabledCell = (parameters: GridRenderCellParams<User>) => {
  const { isUserCurrentUser } = useGetCurrentUser();
  const isCurrentUser = isUserCurrentUser(parameters.row);

  const { showToast } = useToast();
  const { updateUser, isUpdating } = useUpdateUser({ userId: parameters.row.id });

  return (
    <Tooltip title={isCurrentUser ? CANNOT_UPDATE_CURRENT_USER : ''}>
      <Box>
        <Checkbox
          checked={!!parameters.row.settings.userEnabled}
          disabled={isUpdating || isCurrentUser}
          onChange={event => {
            updateUser(
              { enabled: event.target.checked },
              {
                onSuccess: () => {
                  parameters.api.updateRows([
                    {
                      ...parameters.row,
                      settings: {
                        ...parameters.row.settings,
                        userEnabled: !event.target.checked,
                      },
                    },
                  ]);
                  showToast({
                    message: 'User enabled status updated',
                    type: 'success',
                  });
                },
                onError: () =>
                  showToast({
                    message: 'Failed to update enabled status',
                    type: 'error',
                  }),
              }
            );
          }}
        />
      </Box>
    </Tooltip>
  );
};

const ActionsCell = (parameters: GridRenderCellParams<User>) => {
  const { showToast } = useToast();
  const { isUserCurrentUser } = useGetCurrentUser();
  const isCurrentUser = isUserCurrentUser(parameters.row);

  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <Tooltip title={isCurrentUser ? CANNOT_UPDATE_CURRENT_USER : ''}>
        <Box>
          <IconButton onClick={() => setIsOpen(true)} disabled={isCurrentUser}>
            <DeleteIcon />
          </IconButton>
        </Box>
      </Tooltip>
      <DeleteUserModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        user={parameters.row}
        onSuccess={() => {
          setIsOpen(false);
          showToast({ message: 'User deleted', type: 'success' });
          parameters.api.updateRows([
            {
              ...parameters.row,
              _action: 'delete',
            },
          ]);
        }}
      />
    </>
  );
};

const columns: CopiableGridColDef<User>[] = [
  {
    field: 'email',
    headerName: 'Email',
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: (parameters: GridRenderCellParams<User>) => {
      return <Ellipsis text={parameters.row.email} />;
    },
    getCopyValue: row => row.email,
  },
  {
    field: 'role',
    headerName: 'Role',
    width: 120,
    sortable: false,
    filterable: false,
    renderCell: (parameters: GridRenderCellParams<User>) => <RoleCell {...parameters} />,
  },
  {
    field: 'userEnabled',
    headerName: 'Enable',
    width: 70,
    sortable: false,
    filterable: false,
    renderCell: (parameters: GridRenderCellParams<User>) => <EnabledCell {...parameters} />,
  },
  {
    field: 'actions',
    headerName: 'Action',
    width: 65,
    sortable: false,
    filterable: false,
    renderCell: (parameters: GridRenderCellParams<User>) => <ActionsCell {...parameters} />,
  },
];

export const UsersTable = () => {
  const { fetchData, error } = useFetchTenantUsers();
  const [isAddUserOpen, setAddUserOpen] = useState(false);
  const gridApi = useGridApiRef();

  const {
    contextMenuPosition,
    contextMenuClickedItem,
    contextMenuClickedField,
    handleGridContextMenu,
    handleCloseContextMenu,
  } = useContextMenu<User>({
    canUseContextMenu: true,
    getRowById: (rowId: string | number) => gridApi.current?.getRow<User>(rowId) ?? undefined,
  });

  return (
    <Box onContextMenu={handleGridContextMenu}>
      <DataGridLazy<User, { users: User[] }>
        apiRef={gridApi}
        disableColumnMenu
        columns={columns}
        fetchRows={fetchData}
        dataKey='users'
        rowHeight={80}
        error={error}
        showToolbar={true}
        density='compact'
        sx={{
          maxHeight: '700px',
          width: '100%',
          padding: '24px',
          borderRadius: '12px',
        }}
        slotProps={{
          root: { 'data-testid': 'users-table' },
        }}
        slots={{
          toolbar: () => (
            <HeaderBar>
              <TitleText>Users</TitleText>
              <TitleTextActionButton onClick={() => setAddUserOpen(true)}>
                <Add sx={{ padding: '2px' }} />
              </TitleTextActionButton>
              <AddUserModal isOpen={isAddUserOpen} onClose={() => setAddUserOpen(false)} />
            </HeaderBar>
          ),
        }}
      />
      {getCopyValueFromColumn(columns, contextMenuClickedItem, contextMenuClickedField) && (
        <BaseContextMenu anchorPosition={contextMenuPosition} onClose={handleCloseContextMenu}>
          <CopyMenuItem
            columns={columns}
            row={contextMenuClickedItem}
            field={contextMenuClickedField}
            onCopy={handleCloseContextMenu}
          />
        </BaseContextMenu>
      )}
    </Box>
  );
};
